import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { ExternalFinancialTransaction, FinancialTransactionOption } from '../types';
import { formatCurrency } from '../util/formatCurrency';
import AddExternalFinancialTransactionModal from '../modals/AddExternalFinancialTransactionModal';
import MatchExternalTransactionModal from '../modals/MatchExternalTransactionModal';
import { useAuth } from '../hooks/useAuth';

const PERSONAL_ACCOUNT_SOURCES = ['ben_personal_bank', 'isaac_personal_bank'];

const CATEGORY_SOURCE_BADGE: Record<string, string> = {
  ai: 'bg-amber-100 text-amber-800',
  rule: 'bg-gray-100 text-gray-600',
  manual: 'bg-blue-100 text-blue-800',
};

const CATEGORY_SOURCE_OPTIONS: FinancialTransactionOption[] = [
  { key: 'rule', label: 'Auto (Rule)' },
  { key: 'ai', label: 'Auto (AI)' },
  { key: 'manual', label: 'Manual' },
];

const ExternalFinancialTransactions = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<ExternalFinancialTransaction[]>([]);
  const [sources, setSources] = useState<FinancialTransactionOption[]>([]);
  const [categories, setCategories] = useState<FinancialTransactionOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategorizing, setIsCategorizing] = useState(false);

  // All filters + pagination are stored in the URL so the view survives a
  // refresh and can be shared/bookmarked.
  const searchTerm = searchParams.get('customer_search') || '';
  const sourceFilter = searchParams.get('source') || '';
  const categoryFilter = searchParams.get('category') || '';
  const categorySourceFilter = searchParams.get('category_source') || '';
  const matchedFilter = searchParams.get('matched') || '';
  const confirmedFilter = searchParams.get('confirmed') || '';
  const startDate = searchParams.get('start_date') || '';
  const endDate = searchParams.get('end_date') || '';
  const importIdFilter = searchParams.get('import_id') || '';
  const currentPage = (() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  })();
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<ExternalFinancialTransaction | null>(null);
  const [matchedTransaction, setMatchedTransaction] = useState<ExternalFinancialTransaction | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);

  useEffect(() => {
    if (openActionMenuId === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.action-menu')) {
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionMenuId]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [sourcesRes, categoriesRes] = await Promise.all([
          fetchWithAuth('/external_financial_transactions/sources', {
            headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }
          }),
          fetchWithAuth('/external_financial_transactions/categories', {
            headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }
          })
        ]);
        if (sourcesRes.ok) setSources(await sourcesRes.json());
        if (categoriesRes.ok) setCategories(await categoriesRes.json());
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (searchTerm) query.append('customer_search', searchTerm);
        if (sourceFilter) query.append('source', sourceFilter);
        if (categoryFilter) query.append('category', categoryFilter);
        if (categorySourceFilter) query.append('category_source', categorySourceFilter);
        if (matchedFilter) query.append('matched', matchedFilter);
        if (confirmedFilter) query.append('confirmed', confirmedFilter);
        if (startDate) query.append('start_date', startDate);
        if (endDate) query.append('end_date', endDate);
        query.append('page', String(currentPage));
        query.append('per_page', '100');
        if (importIdFilter) query.append('import_id', importIdFilter);

        const res = await fetchWithAuth(`/external_financial_transactions?${query.toString()}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        });

        if (!res.ok) {
          throw new Error('Failed to fetch external financial transactions');
        }

        const data = await res.json();
        setTransactions(data.external_financial_transactions);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } catch (error) {
        console.error('Error fetching external financial transactions:', error);
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [searchTerm, sourceFilter, categoryFilter, categorySourceFilter, matchedFilter, confirmedFilter, startDate, endDate, currentPage, refetchCounter, importIdFilter]);

  // Set/clear a filter param and reset to the first page. `replace` avoids
  // pushing a history entry per keystroke for the live-typing search input.
  const setFilter = (key: string, value: string, replace = false) => {
    setSearchParams(prev => {
      if (value) {
        prev.set(key, value);
      } else {
        prev.delete(key);
      }
      prev.set('page', '1');
      return prev;
    }, { replace });
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const date = new Date(isDateOnly ? `${dateString}T00:00:00` : dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  };

  const formatAmount = (amount: string) => {
    const value = parseFloat(amount);
    if (isNaN(value)) return amount;
    const formatted = formatCurrency(Math.abs(value));
    return value < 0 ? `-${formatted}` : formatted;
  };

  const updateTransactionInPlace = (id: number, changes: Partial<ExternalFinancialTransaction>) => {
    setTransactions((prev) => prev.map((t) => (t.id === id ? { ...t, ...changes } : t)));
  };

  // Inline edits PATCH a single field and update that row in local state only,
  // so the list never refetches/reorders and the admin keeps their scroll position.
  const handleInlineUpdate = async (
    txn: ExternalFinancialTransaction,
    field: 'confirmed' | 'category',
    value: boolean | string
  ) => {
    const previous: Partial<ExternalFinancialTransaction> =
      field === 'confirmed'
        ? { confirmed: txn.confirmed }
        : { category: txn.category, category_label: txn.category_label, category_source: txn.category_source, category_source_label: txn.category_source_label };

    // Optimistic update.
    if (field === 'confirmed') {
      updateTransactionInPlace(txn.id, { confirmed: value as boolean });
    } else {
      const categoryKey = (value as string) || null;
      const label = categoryKey ? categories.find((c) => c.key === categoryKey)?.label ?? null : null;
      updateTransactionInPlace(txn.id, {
        category: categoryKey,
        category_label: label,
        category_source: categoryKey ? 'manual' : null,
        category_source_label: categoryKey ? 'Manual' : null,
      });
    }

    try {
      const body =
        field === 'confirmed'
          ? { external_financial_transaction: { confirmed: value } }
          : { external_financial_transaction: { category: (value as string) || null } };

      const res = await fetchWithAuth(`/external_financial_transactions/${txn.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Update failed');
    } catch (error) {
      console.error('Error updating transaction:', error);
      updateTransactionInPlace(txn.id, previous);
      toast.error('Failed to update transaction');
    }
  };

  const handleAutoCategorize = async () => {
    setIsCategorizing(true);
    try {
      const query = new URLSearchParams();
      if (sourceFilter) query.append('source', sourceFilter);
      if (importIdFilter) query.append('import_id', importIdFilter);
      const qs = query.toString();

      const res = await fetchWithAuth(
        `/external_financial_transactions/auto_categorize${qs ? `?${qs}` : ''}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }
        }
      );

      if (!res.ok) throw new Error('Failed to queue auto-categorisation');

      toast.success('Auto-categorisation queued — refresh in a moment to see results.');
    } catch (error) {
      console.error('Error queuing auto-categorisation:', error);
      toast.error('Failed to queue auto-categorisation');
    } finally {
      setIsCategorizing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bank & External Transactions</h1>
          <p className="text-gray-600">Browse all bank statement lines and other external financial transactions</p>
          {importIdFilter && (
            <div className="mt-3 inline-flex items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              <span>Filtered to transactions from import #{importIdFilter}</span>
              <button
                type="button"
                onClick={() => setFilter('import_id', '')}
                className="text-blue-700 hover:text-blue-900 underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
          {isAdmin && (
            <button
              type="button"
              onClick={handleAutoCategorize}
              disabled={isCategorizing}
              className="self-start bg-black text-white px-4 py-2 rounded hover:bg-gray-800 whitespace-nowrap disabled:opacity-50"
            >
              {isCategorizing ? 'Queuing…' : 'Auto-categorise uncategorised'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="self-start bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 whitespace-nowrap"
          >
            + Add Transaction
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setFilter('customer_search', e.target.value, true)}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              id="source"
              value={sourceFilter}
              onChange={(e) => setFilter('source', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {sources.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="category"
              value={categoryFilter}
              onChange={(e) => setFilter('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="uncategorised">Uncategorised</option>
              {categories.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="categorySource" className="block text-sm font-medium text-gray-700 mb-1">
              Category Source
            </label>
            <select
              id="categorySource"
              value={categorySourceFilter}
              onChange={(e) => setFilter('category_source', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {CATEGORY_SOURCE_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="matched" className="block text-sm font-medium text-gray-700 mb-1">
              Match Status
            </label>
            <select
              id="matched"
              value={matchedFilter}
              onChange={(e) => setFilter('matched', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Matched</option>
              <option value="false">Unmatched</option>
            </select>
          </div>

          <div>
            <label htmlFor="confirmed" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmed
            </label>
            <select
              id="confirmed"
              value={confirmedFilter}
              onChange={(e) => setFilter('confirmed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Confirmed</option>
              <option value="false">Unconfirmed</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setFilter('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setFilter('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {totalCount} transaction{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="hidden md:table-cell px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                {isAdmin && (
                  <th className="px-2 md:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confirmed
                  </th>
                )}
                {isAdmin && (
                  <th className="px-2 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 6} className="px-6 py-12 text-center text-gray-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 6} className="px-6 py-12 text-center text-gray-500">
                    No external financial transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => {
                  const amountValue = parseFloat(txn.amount);
                  const amountClass = !isNaN(amountValue) && amountValue < 0
                    ? 'text-red-600'
                    : 'text-gray-900';
                  const isMenuOpen = openActionMenuId === txn.id;
                  return (
                    <tr key={txn.id} className={`hover:bg-gray-50 ${isMenuOpen ? 'bg-yellow-100' : ''}`}>
                        <td className="hidden md:table-cell px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(txn.date)}
                        </td>
                        <td className="px-2 md:px-6 py-4 text-sm text-gray-900 max-w-xs break-words">
                          {txn.description}
                        </td>
                        <td className={`px-2 md:px-6 py-4 text-sm whitespace-nowrap ${amountClass}`}>
                          {formatAmount(txn.amount)}
                        </td>
                        <td className="hidden md:table-cell px-2 md:px-6 py-4 text-sm text-gray-500">
                          {txn.finance_source_label}
                        </td>
                        <td className="hidden md:table-cell px-2 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {isAdmin ? (
                              <select
                                aria-label="Transaction category"
                                value={txn.category || ''}
                                onChange={(e) => handleInlineUpdate(txn, 'category', e.target.value)}
                                className="w-full min-w-[14rem] px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Uncategorised</option>
                                {categories.map((c) => (
                                  <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span>{txn.category_label || '—'}</span>
                            )}
                            {txn.category_source && (
                              <span
                                className={`inline-block w-fit px-2 py-0.5 text-xs font-medium rounded-full ${CATEGORY_SOURCE_BADGE[txn.category_source] || 'bg-gray-100 text-gray-600'}`}
                              >
                                {txn.category_source_label || txn.category_source}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                          {txn.notes || '—'}
                        </td>
                        {isAdmin && (
                          <td className="px-2 md:px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              aria-label="Confirmed"
                              checked={txn.confirmed}
                              onChange={(e) => handleInlineUpdate(txn, 'confirmed', e.target.checked)}
                              className="h-4 w-4 cursor-pointer"
                            />
                          </td>
                        )}
                        {isAdmin && (
                          <td className="px-2 md:px-6 py-4 text-right text-sm relative overflow-visible">
                            <div className="action-menu relative inline-block text-left">
                              <button
                                type="button"
                                onClick={() => setOpenActionMenuId(openActionMenuId === txn.id ? null : txn.id)}
                                className="p-1 rounded hover:bg-gray-100"
                                aria-label="Open actions menu"
                              >
                                <span className="text-xl">⋯</span>
                              </button>
                              {openActionMenuId === txn.id && (
                                <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded shadow-md z-10">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedTransaction(txn);
                                      setIsAddModalOpen(true);
                                      setOpenActionMenuId(null);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMatchedTransaction(txn);
                                      setIsMatchModalOpen(true);
                                      setOpenActionMenuId(null);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    Match
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <MatchExternalTransactionModal
        isOpen={isMatchModalOpen}
        externalTransaction={matchedTransaction}
        onClose={() => {
          setIsMatchModalOpen(false);
          setMatchedTransaction(null);
        }}
        onChange={() => setRefetchCounter((c) => c + 1)}
      />

      <AddExternalFinancialTransactionModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedTransaction(null);
        }}
        onSuccess={() => {
          if (selectedTransaction) {
            setRefetchCounter((c) => c + 1);
          } else if (currentPage !== 1) {
            handlePageChange(1);
          } else {
            setRefetchCounter((c) => c + 1);
          }
        }}
        sources={sources.filter(s => (
          !PERSONAL_ACCOUNT_SOURCES.includes(s.key)
          || s.key === selectedTransaction?.finance_source
        ))}
        categories={categories}
        selectedTransaction={selectedTransaction}
      />
    </div>
  );
};

export default ExternalFinancialTransactions;
